// Google Play In-App Updates (Flexible flow).
// Detects a newer AAB on the Play Store, prompts gently, downloads in the
// background while the driver keeps using the app, then surfaces a "restart to
// update" action. No-ops on web and on older native builds that don't yet bundle
// the plugin (the dynamic import / bridge call simply fails and is ignored).
import { Capacitor } from '@capacitor/core';

// Play enums we care about (avoids importing the enum just for two constants).
const UPDATE_AVAILABLE = 2;        // AppUpdateAvailability.UPDATE_AVAILABLE
const FLEXIBLE_DOWNLOADED = 11;    // FlexibleUpdateInstallStatus.DOWNLOADED

let started = false;

// Kick off a flexible update check. `onReadyToInstall` fires once the new version
// has finished downloading in the background, so the UI can offer a restart.
export async function checkForFlexibleUpdate(onReadyToInstall: () => void): Promise<void> {
  if (started || !Capacitor.isNativePlatform()) return;
  started = true;
  try {
    const { AppUpdate } = await import('@capawesome/capacitor-app-update');
    const info = await AppUpdate.getAppUpdateInfo();
    if (info.updateAvailability !== UPDATE_AVAILABLE || !info.flexibleUpdateAllowed) return;

    await AppUpdate.addListener('onFlexibleUpdateStateChange', (state: any) => {
      if (state?.installStatus === FLEXIBLE_DOWNLOADED) onReadyToInstall();
    });
    // Shows Play's gentle update dialog; downloads in the background if accepted.
    await AppUpdate.startFlexibleUpdate();
  } catch {
    // Web, or an installed AAB built before the plugin was added - ignore.
    started = false;
  }
}

// Apply a downloaded flexible update (restarts the app into the new version).
export async function completeFlexibleUpdate(): Promise<void> {
  try {
    const { AppUpdate } = await import('@capawesome/capacitor-app-update');
    await AppUpdate.completeFlexibleUpdate();
  } catch {}
}
